// LoginPage — MD3 форма логина (username/password) + submit + error display + logo.
import { useState, type FormEvent } from 'react';
import { Box, Paper, TextField, Button, Typography, Alert, Stack, CircularProgress, Link } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Logo } from '@/components/Logo';

/** MD3 форма входа в систему. */
export function LoginPage(): JSX.Element {
  const { login, status, error } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Если уже аутентифицирован — перенаправляем на /dashboard
  if (status === 'authenticated') {
    navigate('/dashboard', { replace: true });
  }

  const canSubmit = username.trim().length > 0 && password.length > 0 && !submitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const ok = await login({ username: username.trim(), password });
      if (ok) {
        navigate('/dashboard', { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Stack spacing={3} alignItems="center">
          <Logo size={80} alt="Упряжка logo" />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            Вход в Упряжку
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Введите учётные данные для входа в систему
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Stack spacing={2}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              <TextField
                label="Имя пользователя"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                autoFocus
                required
                fullWidth
                inputProps={{ 'aria-label': 'Имя пользователя' }}
              />
              <TextField
                label="Пароль"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                fullWidth
                inputProps={{ 'aria-label': 'Пароль' }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
                aria-label="Войти"
              >
                Войти
              </Button>
              {/* R68 / V12 U-36: с входа — ссылка на публичный лендинг (что такое Упряжка). */}
              <Typography variant="body2" textAlign="center">
                <Link component={RouterLink} to="/main">
                  Что такое Упряжка?
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
